import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import VideoCard from "@/components/base/VideoCard";
import type { Video } from "@/types/video";
import { getAllVideos } from "@/utils/video";

export default function Favorites() {
  const { favorites } = useFavorites();

  useDocumentMeta({
    title: "我的收藏 - 麻豆免费视频",
    description: "查看你在麻豆免费视频收藏的电影、电视剧和综艺内容。收藏功能为前端演示，数据保存在本地浏览器。",
    keywords: "我的收藏,麻豆免费视频,影视收藏,在线观看",
    canonicalPath: "/favorites",
  });

  const allVideos = getAllVideos();
  const list: Video[] = favorites
    .map((id) => allVideos.find((video) => video.id === id))
    .filter((video): video is Video => Boolean(video))
    .reverse();

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10 lg:px-10">
      <div className="rounded-xl border border-background-800 bg-gradient-to-r from-background-900 to-primary-950/30 p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-950">
            <i className="ri-heart-3-fill text-xl"></i>
          </span>
          <div>
            <h1 className="text-2xl font-bold text-foreground-50 md:text-3xl">我的收藏</h1>
            <p className="mt-1 text-sm text-foreground-400">
              共 {list.length} 部影视 · 收藏为前端演示，保存在本地浏览器
            </p>
          </div>
        </div>
      </div>

      {list.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-6">
          {list.map((video, i) => (
            <VideoCard key={video.id} video={video} index={i} showType />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-700 bg-background-900/60 px-6 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-500">
            <i className="ri-heart-3-line text-3xl"></i>
          </span>
          <h3 className="mt-5 text-lg font-semibold text-foreground-100">还没有收藏任何内容</h3>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            在影视详情页点击「收藏」，就能把喜欢的内容加入这里，方便下次快速找到。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/category/movie"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-film-line"></i>
              去看电影
            </Link>
            <Link
              to="/category/tv"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-6 py-3 text-sm font-medium text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
            >
              <i className="ri-tv-2-line"></i>
              去看电视剧
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}